import React, {
  memo,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { Table, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'appkit-react';
import { Prompt, useHistory } from 'react-router-dom';
import GuidingPrinciplesForm from '../systemSetting/guidingPrinciples';
import { columns } from '../systemSetting/guidingPrinciples/configs/columns.config';
import Dropdown from './insightsDropdown';
import {
  GUIDING_PRINCIPLES_DIVESTITURES_KEY,
  GUIDING_PRINCIPLES_DIVESTITURES_TITLE,
  GUIDING_PRINCIPLES_KEY,
  GUIDING_PRINCIPLES_TITLE,
} from '@model/insights';
import GuideDrawer from './guideDrawer';
import SectionComment from 'src/ddvComment/SectionComment';
import CommentAnchor from 'src/ddvComment/directUser/CommentAnchor';
import actions from '@flux/action';

// import '@style/modules/insights/guidingPrinciples.scss';
import '../../styles/modules/insights/guidingPrinciples.scss';
import alert from '@components/alert';
import RichEditor from '@modules/questionnaires/complexity/richEditor';
import { getAllProjectInstances } from '@service/apis';

const numberOfLinesExceedsLimit = (limitOfLines) => (elemId) => {
  const el = document.getElementById(elemId);
  if (el === null) {
    return false;
  }
  const divHeight = el.offsetHeight;

  const lineHeight = parseInt(
    window.getComputedStyle(el, null).getPropertyValue('line-height'),
  );
  const lines = divHeight / lineHeight;

  return lines >= limitOfLines;
};

const getCurrentModuleTitle = (instanceType, pathname) => {
  if (instanceType === 'Customize') {
    return 'Guiding Principles';
  } else if (pathname === '/insights/guidingPrinciples') {
    return GUIDING_PRINCIPLES_TITLE;
  } else if (pathname === '/insights/guidingPrinciples-divestitures') {
    return GUIDING_PRINCIPLES_DIVESTITURES_TITLE;
  }
};

const getCurrentModuleKey = (instanceType, pathname) => {
  if (
    instanceType === 'Customize' ||
    pathname === '/insights/guidingPrinciples'
  ) {
    return GUIDING_PRINCIPLES_KEY;
  } else if (pathname === '/insights/guidingPrinciples-divestitures') {
    return GUIDING_PRINCIPLES_DIVESTITURES_KEY;
  }
};

const ExpandableCell = ({
  isExpanded = false,
  children,
  className,
  index,
  onClickButton,
  markExpandableCell,
  setIsTableTheadExpandButtonShow,
}) => {
  const expandIconId = `expand-icon-${index}`;
  const textId = `gd-description-${index}`;
  const [shouldShowExpandButton, setShouldShowExpandButton] = useState(false);

  useEffect(() => {
    // calculate height after rendered
    setTimeout(() => {
      if (numberOfLinesExceedsLimit(4)(textId)) {
        setShouldShowExpandButton(true);
        setIsTableTheadExpandButtonShow(true);
        markExpandableCell(index);
      }
    }, 0);
  }, []);

  const ExpandButton = ({ isExpanded, onClick }) => (
    <span
      className={`appkiticon icon-down-chevron-fill a-font-10  ${
        isExpanded ? 'arrow-up' : 'arrow-down'
      }`}
      id={expandIconId}
      onClick={onClick}
    />
  );
  return (
    <>
      <div
        className={
          shouldShowExpandButton && !isExpanded
            ? `${className}-ellipsis`
            : `${className}`
        }
        id={textId}
      >
        {children}
      </div>
      {shouldShowExpandButton ? (
        <ExpandButton isExpanded={isExpanded} onClick={onClickButton} />
      ) : null}
    </>
  );
};

const ExpandAllButton = ({ setAll, areExpandedArray, areExpandableArray }) => {
  const expandAll = setAll(true);
  const collapseAll = setAll(false);

  const [state, setState] = useState(true);

  useEffect(() => {
    const isExpandableCell = (i) => areExpandableArray.includes(i);
    const allExpandableCellCollapsed = areExpandedArray.every(
      (e, i) => !isExpandableCell(i) || e === false,
    );
    if (allExpandableCellCollapsed) {
      setState(true);
    } else {
      setState(false);
    }
  }, [areExpandedArray, areExpandableArray]);

  const onClick = state ? expandAll : collapseAll;

  return (
    <span
      className="expand-all-button"
      onClick={() => {
        onClick();
        setState((prev) => !prev);
      }}
    >
      {state ? 'Expand All' : 'Collapse All'}
    </span>
  );
};

const GuidingPrincipleTable = ({
  data,
  isTableTheadExpandButtonShow,
  setIsTableTheadExpandButtonShow,
}) => {
  const [areExpandedArray, setAreExpandedArray] = useState([]);
  const [areExpandableArray, setAreExpandableArray] = useState([]);

  useEffect(() => {
    setAreExpandedArray(data.map((_) => false));
  }, [data]);

  const setEachRowExpand = (i) => () =>
    setAreExpandedArray((prevState) =>
      prevState.map((e, index) => (index !== i ? e : !e)),
    );
  const markExpandableCell = (index) =>
    setAreExpandableArray((prev) =>
      [...prev, index].filter((e, i, a) => a.indexOf(e) === i),
    );

  const setAll = (val) => () =>
    setAreExpandedArray((prevState) => prevState.map((_) => val));

  const enhanceColumns = (cols) =>
    cols.slice(1, 3).map((col, i) =>
      Object.assign(
        { ...col },
        {
          render: (v, _, idx) => (
            <Tooltip>
              {i === 0 ? (
                <div className="gd-title ellipsis-text">
                  <RichEditor value={v} readOnly />
                </div>
              ) : (
                <ExpandableCell
                  className="gd-description"
                  index={idx}
                  isExpanded={areExpandedArray[idx]}
                  onClickButton={setEachRowExpand(idx)}
                  markExpandableCell={markExpandableCell}
                  setIsTableTheadExpandButtonShow={
                    setIsTableTheadExpandButtonShow
                  }
                >
                  <RichEditor value={v} readOnly />
                </ExpandableCell>
              )}
            </Tooltip>
          ),
          width: i === 0 ? '40%' : 0,
          title:
            i === 0 ? (
              'Title'
            ) : (
              <>
                <span>Description</span>
                {isTableTheadExpandButtonShow ? (
                  <ExpandAllButton
                    setAll={setAll}
                    areExpandedArray={areExpandedArray}
                    areExpandableArray={areExpandableArray}
                  />
                ) : null}
              </>
            ),
        },
      ),
    );

  return (
    <Table
      columns={enhanceColumns([...columns])}
      dataSource={data}
      pagination={false}
      sticky
      tableLayout="fixed"
      scroll={{ y: '70vh' }}
      rowKey="id"
    />
  );
};

const GuidingPrinciplesComps = ({ location, currentSubmoduleName }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isTableTheadExpandButtonShow, setIsTableTheadExpandButtonShow] =
    useState(false);
  const [isDrawerExpanded, toggleDrawerExpander] = useState(true);
  const [isDrawerFullExpand, setIsDrawerFullExpand] = useState(false);
  const [projectInstanceList, setProjectInstanceList] = useState([]);
  const { projectId, companyInstance, instanceType } = useSelector(
    ({ common }) => ({
      projectId: common.currentId,
      companyInstance: common.projectCompanyInfo.companyInstance,
      instanceType: common.projectCompanyInfo.instanceType,
    }),
  );
  // const loc = useLocation();
  // const isIntegration = instanceType !== 'Divestiture';
  // const type = isIntegration ? 'Integration' : 'Divestiture';
  const dispatch = useDispatch();
  const gpRef = useRef();
  const history = useHistory();

  const companyInstanceForRequest = useMemo(() => {
    if (instanceType && instanceType !== 'Customize') {
      if (location.pathname === '/insights/guidingPrinciples') {
        return projectInstanceList.find((i) => i.type === 'Integration')?.id;
      }
      if (location.pathname === '/insights/guidingPrinciples-divestitures') {
        return projectInstanceList.find((i) => i.type === 'Divestiture')?.id;
      }
    }
    return companyInstance;
  }, [projectInstanceList, location.pathname, instanceType, companyInstance]);

  useEffect(() => {
    if (!companyInstanceForRequest) {
      return;
    }
    dispatch(
      actions.fetchGdPrincipleAndInsightstData(
        projectId,
        companyInstanceForRequest,
      ),
    );
    dispatch(
      actions.fetchGdPrincipleDrawerData(projectId, companyInstanceForRequest),
    );
  }, [dispatch, isEditing, projectId, companyInstanceForRequest]);

  useEffect(() => {
    setIsTableTheadExpandButtonShow(false);
  }, [isEditing]);

  const fetchProjectInstanceList = useCallback(async () => {
    try {
      actions.changeLoading(true);
      const result = await getAllProjectInstances();
      setProjectInstanceList(result);
    } catch (e) {
      console.log(e);
    }
  }, []);
  useEffect(() => {
    if (instanceType !== 'Customize') {
      fetchProjectInstanceList();
    }
  }, [projectId, instanceType]);

  const gdPrcplData = useSelector(
    ({ insights }) => insights?.insightsData[projectId]?.principleData || [],
  );

  const drawerData = useSelector(
    ({ insights }) => insights.insightsData[projectId]?.principleDrawerData,
  );
  const [drawerKey, setDrawerKey] = useState(0);
  useEffect(() => {
    setDrawerKey(drawerKey + 1);
  }, [drawerData]);

  const { moduleTitle, moduleKey } = useMemo(
    () => ({
      // moduleTitle: getCurrentModuleTitle(instanceType, location.pathname),
      moduleKey: getCurrentModuleKey(instanceType, location.pathname),
    }),
    [instanceType, location.pathname],
  );

  const isValidGuidingP = (r) => r.guidingPrinciple || r.title;
  const fillIfUndefined = (r) =>
    Object.assign({}, r, {
      guidingPrinciple: r.guidingPrinciple || '',
      title: r.title || '',
    });

  const handleSave = ({ guidingPrincipleList: newArray }) =>
    dispatch(
      actions.updateAllGuidingPrinciples(
        {
          principleList: newArray.filter(isValidGuidingP).map(fillIfUndefined),
        },
        projectId,
        companyInstanceForRequest,
      ),
    );
  const isInternal = JSON.parse(
    window.localStorage.userInfo || '{}',
  ).isInternal;

  const handleEditBack = () => {
    alert.confirm({
      onOk: async () => {
        // history.push('/projects/list');
        await gpRef.current.save();
      },
      okText: 'yes',
      content: 'Do you want to save all the changes?',
      onCancel: () => {
        setIsEditing(false);
      },
      async: true,
      noCloseIcon: true,
      maskClosable: false,
    });
  };

  const handlePrompt = (location) => {
    if (isEditing) {
      alert.confirm({
        onOk: async () => {
          // history.push('/projects/list');
          await gpRef.current.save();
          history.push(location.pathname);
        },
        okText: 'yes',
        content: 'Do you want to save all the changes?',
        onCancel: async (close) => {
          setIsEditing(false);
          setTimeout(() => {
            history.push(location.pathname + location.search);
          }, 0);
          // close();
        },
        async: true,
        noCloseIcon: true,
        maskClosable: false,
      });
      return false;
    }
    return true;
  };
  if (!isEditing) {
    return (
      <>
        {drawerData && (
          <GuideDrawer
            isExpand={isDrawerExpanded}
            onExpand={toggleDrawerExpander}
            isFullExpand={isDrawerFullExpand}
            setIsFullExpand={setIsDrawerFullExpand}
            drawerData={drawerData}
            projectId={projectId}
            type={instanceType}
            companyInstance={companyInstanceForRequest}
            key={drawerKey}
            isLeftPartEditing={false}
          />
        )}

        <Dropdown section={moduleKey} />
        <div
          className={`guiding-principle-table-wrapper ${
            isDrawerExpanded ? 'collapsed' : ''
          }`}
        >
          <SectionComment
            module={currentSubmoduleName}
            placement="bottomRight"
          />
          {isInternal && (
            <Button
              className="manage-button role-control"
              onClick={() => setIsEditing(true)}
              style={{ textTransform: 'capitalize' }}
            >
              Edit
            </Button>
          )}
          <GuidingPrincipleTable
            data={gdPrcplData}
            key={gdPrcplData}
            isTableTheadExpandButtonShow={isTableTheadExpandButtonShow}
            setIsTableTheadExpandButtonShow={setIsTableTheadExpandButtonShow}
          />
        </div>
      </>
    );
  }
  return (
    <>
      {drawerData && (
        <GuideDrawer
          isExpand={isDrawerExpanded}
          onExpand={toggleDrawerExpander}
          isFullExpand={isDrawerFullExpand}
          setIsFullExpand={setIsDrawerFullExpand}
          drawerData={drawerData}
          projectId={projectId}
          type={instanceType}
          companyInstance={companyInstanceForRequest}
          key={drawerKey}
          isLeftPartEditing={true}
        />
      )}
      <div
        className={`guiding-principle-list-page ${
          isDrawerExpanded ? 'collapsed' : ''
        }`}
      >
        <Prompt message={handlePrompt} />
        <span className="dd-back-btn" onClick={handleEditBack}>
          <span className="appkiticon icon-left-chevron-fill a-font-12" /> Back
        </span>
        <div className="manage-principles-title">Manage Guiding Principles</div>
        <GuidingPrinciplesForm
          _goBack={() => setIsEditing(false)}
          fetchAction={() =>
            actions.fetchGdPrincipleAndInsightstData(
              projectId,
              companyInstanceForRequest,
            )
          }
          saveAction={(e) => handleSave(e)}
          gdPrcplList={gdPrcplData}
          ref={gpRef}
          setIsEditing={setIsEditing}
        />
      </div>
    </>
  );
};

const GuidingPrinciplesPage = (props) => (
  <div className="insights-guiding-principles">
    <GuidingPrinciplesComps {...props} key={props.currentSubmoduleName} />
  </div>
);

export default CommentAnchor(memo(GuidingPrinciplesPage), {
  shouldScrollInToView: (anchorRecord, anchorId) =>
    anchorRecord?.moduleName === anchorId,
  // initialAnchorId: 'Integration Guiding Principles',
  getAnchorIdFromProp(props) {
    return props.currentSubmoduleName;
  },
});
